import React from 'react';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import { AdvisorUsageTable } from '@/components/usage-analytics/AdvisorUsageTable';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';
import { useAdvisorUsageAnalytics } from '@/hooks/useAdvisorUsageAnalytics';
import { useAdminMode } from '@/hooks/useAdminMode';
import { AdminModeBanner } from '@/components/admin/AdminModeBanner';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';

function ErrorState({ error }: { error: string }) {
  return (
    <Alert
      variant="destructive"
      className="mt-4"
    >
      <AlertDescription>
        <div className="space-y-4">
          <p className="font-semibold">Error Loading Usage Data</p>
          <p>{error}</p>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function AdvisorUsageAnalytics() {
  // Admin mode state
  const { selectedAdvisorId, adminModeActive, hasSelectedAdvisor } = useAdminMode();

  // In admin mode, use selectedAdvisorId; otherwise use undefined (defaults to session advisor)
  const targetAdvisorId = adminModeActive ? (selectedAdvisorId ?? undefined) : undefined;

  const { usageAnalytics, calculatedMetrics, advisorsList, activeAdvisorsCount, refetch } =
    useAdvisorUsageAnalytics(targetAdvisorId);

  // Show usage UI only when not in admin mode OR when advisor is selected
  const showUsageUI =
    !adminModeActive || (adminModeActive && hasSelectedAdvisor && !usageAnalytics.isLoading);

  const isLoading = usageAnalytics.isLoading;

  return (
    <>
      <AdminModeBanner />

      <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <DashboardPageHeader title="Usage Dashboard" />

        {/* Error State */}
        {!isLoading && usageAnalytics.errorMessage && (
          <ErrorState error={usageAnalytics.errorMessage} />
        )}

        {/* Usage Dashboard UI */}
        {(showUsageUI || isLoading) && !usageAnalytics.errorMessage && (
          <>
            {/* Top Metrics Bar */}
            <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <TooltipProvider>
                <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
                  <MetricCard
                    label="Active Advisors"
                    value={activeAdvisorsCount}
                    description="Number of advisors who have logged in at least once"
                    isLoading={isLoading}
                  />
                  {USAGE_METRICS_CONFIG.slice(0, 5).map(metricDef => {
                    const rawValue = calculatedMetrics[metricDef.key];
                    let displayValue: string | number;

                    if (metricDef.format === 'currency') {
                      if (rawValue >= 1000000) {
                        displayValue = `$${(rawValue / 1000000).toFixed(2)}M`;
                      } else if (rawValue >= 1000) {
                        displayValue = `$${(rawValue / 1000).toFixed(1)}K`;
                      } else {
                        displayValue = `$${rawValue.toLocaleString()}`;
                      }
                    } else {
                      displayValue = rawValue.toLocaleString();
                    }

                    return (
                      <MetricCard
                        key={metricDef.key}
                        label={metricDef.label}
                        value={displayValue}
                        description={metricDef.description}
                        isLoading={isLoading}
                      />
                    );
                  })}
                </div>
              </TooltipProvider>
            </div>

            {/* Advisors Table */}
            <AdvisorUsageTable
              advisors={advisorsList}
              onRefresh={refetch}
              isRefreshing={isLoading}
            />
          </>
        )}
      </main>
    </>
  );
}

function MetricCard({
  label,
  value,
  description,
  isLoading,
}: {
  label: string;
  value: string | number;
  description?: string;
  isLoading?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <p className="mb-1 text-sm text-gray-500">{label}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-16 rounded" />
          ) : (
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          )}
        </div>
      </TooltipTrigger>
      {description && (
        <TooltipContent>
          <p>{description}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
